import { useState, useEffect, useMemo } from "react";
import { Calculator, Euro, MapPin, Ruler, Home, TrendingUp, TrendingDown, Info, ToggleLeft, ToggleRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { buildTimelinesForProgram, TimelineDisplay } from "@/components/ProjectTimeline";
import { getUserRole } from "@/pages/Login";

// Prix de rachat CEE (€/kWhc)
const PRIX_RACHAT_TRES_MODESTE = 0.0125; // Précarité Bleu
const PRIX_RACHAT_CLASSIQUE = 0.0074;   // Précarité Jaune

// Montants kWhc par zone et surface
const MONTANT_KWHC = {
  H1: { "S < 70": 327600, "70 ≤ S < 90": 458640, "90 ≤ S": 655200 },
  H2: { "S < 70": 273000, "70 ≤ S < 90": 382200, "90 ≤ S": 546000 },
  H3: { "S < 70": 191100, "70 ≤ S < 90": 267540, "90 ≤ S": 382200 }
};

// MPR PAC par précarité
const MPR_PAC = {
  bleu: 5000,
  jaune: 4000
};

// MPR SSC par précarité
const MPR_SSC = {
  bleu: 10000,
  jaune: 8000
};

// MPR CESI par précarité
const MPR_CESI = {
  bleu: 4000,
  jaune: 3000
};

// Taux de financement MPR
const TAUX_FINANCEMENT_MPR = 0.12;

function getRegies(mode, precarite, surfaceNum) {
  if (mode === "lead") return { r174: 2500, rMPR: 2500, r174Cum: 1250, rMPRCum: 1250 };
  if (precarite === "jaune") return { r174: 2500, rMPR: 2500, r174Cum: 1250, rMPRCum: 1250 };
  if (surfaceNum < 70) return { r174: 1000, rMPR: 2000, r174Cum: 1000, rMPRCum: 2000 };
  if (surfaceNum <= 130) return { r174: 1500, rMPR: 2500, r174Cum: 1500, rMPRCum: 2500 };
  return { r174: 2000, rMPR: 3000, r174Cum: 2000, rMPRCum: 3000 };
}

function surfaceToNum(key) {
  if (key === "S < 70") return 60;
  if (key === "70 ≤ S < 90") return 80;
  return 100;
}

export default function Simulateur() {
  // Charges modifiables
  const [charges, setCharges] = useState({
    marchandise_pac_ssc: 6580,
    marchandise_pac: 3000,
    marchandise_pac_cesi: 4917,
    sous_traitant_pac_ssc: 4200,
    sous_traitant_pac: 2000,
    sous_traitant_pac_cesi: 3917,
    admin: 80
  });

  // Mode coût Lead/Régie
  const [regieMode, setRegieMode] = useState("regie");

  // Période — switch: ON = avant 31/12/25, OFF (default) = après 01/01/26
  const [periodeMPRAvant, setPeriodeMPRAvant] = useState(false);
  const periode = periodeMPRAvant ? "before_jan_2026" : "from_jan_2026";
  const isAdmin = getUserRole() === "admin_full" || getUserRole() === "admin_restricted";

  // Sélections
  const [zone, setZone] = useState("H1");
  const [surface, setSurface] = useState("S < 70");
  const [typeChantier, setTypeChantier] = useState("PAC/SSC");
  const [precarite, setPrecarite] = useState("bleu");

  // Régies calculées
  const regies = useMemo(() => getRegies(regieMode, precarite, surfaceToNum(surface)), [regieMode, precarite, surface]);
  const regieMPR = regies.rMPR;

  // Résultats calculés
  const [resultats, setResultats] = useState(null);

  // Calcul automatique
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculerResultats();
  }, [charges, zone, surface, typeChantier, precarite, regieMPR, periode]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculerResultats = () => {
    // Montant kWhc pour cette zone/surface
    const montantKwhc = MONTANT_KWHC[zone][surface];
    
    // Prix de rachat selon précarité
    const prixRachat = precarite === "bleu" ? PRIX_RACHAT_TRES_MODESTE : PRIX_RACHAT_CLASSIQUE;
    
    // CEE = Montant kWhc * Prix rachat
    const cee = montantKwhc * prixRachat;
    
    // MPR selon type de chantier et précarité
    let mpr = 0;
    let totalCharges = 0;
    
    if (typeChantier === "PAC") {
      mpr = precarite === "bleu" ? MPR_PAC.bleu : MPR_PAC.jaune;
      totalCharges = charges.marchandise_pac + charges.sous_traitant_pac + regieMPR + charges.admin;
    } else if (typeChantier === "PAC/SSC") {
      mpr = (precarite === "bleu" ? MPR_PAC.bleu : MPR_PAC.jaune) + 
            (precarite === "bleu" ? MPR_SSC.bleu : MPR_SSC.jaune);
      totalCharges = charges.marchandise_pac_ssc + charges.sous_traitant_pac_ssc + regieMPR + charges.admin;
    } else if (typeChantier === "PAC + CESI") {
      mpr = (precarite === "bleu" ? MPR_PAC.bleu : MPR_PAC.jaune) + 
            (precarite === "bleu" ? MPR_CESI.bleu : MPR_CESI.jaune);
      totalCharges = charges.marchandise_pac_cesi + charges.sous_traitant_pac_cesi + regieMPR + charges.admin;
    }
    
    // Financement MPR (12% du MPR)
    const financementMPR = mpr * TAUX_FINANCEMENT_MPR;
    
    // Total des aides
    const totalAides = mpr + cee;
    
    // Marge
    let marge;
    if (periodeMPRAvant) {
      // Avant 31/12/25 : marge fixe
      marge = precarite === "bleu" ? 6821 : 3321;
    } else {
      // À partir de janvier 2026 : calcul standard
      marge = (totalAides / 1.055) - totalCharges - financementMPR;
    }
    
    setResultats({
      montantKwhc,
      cee: Math.round(cee * 100) / 100,
      mpr,
      financementMPR: Math.round(financementMPR * 100) / 100,
      totalAides: Math.round(totalAides * 100) / 100,
      totalCharges,
      marge: Math.round(marge * 100) / 100
    });
  };

  const handleChargeChange = (key, value) => {
    const numValue = parseFloat(value) || 0;
    setCharges(prev => ({ ...prev, [key]: numValue }));
  };

  const getSurfaceFromMetrage = (metrage) => {
    if (metrage < 70) return "S < 70";
    if (metrage < 90) return "70 ≤ S < 90";
    return "90 ≤ S";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-3">
            <Calculator className="w-8 h-8 text-primary" />
            Simulateur CEE / MPR / Marge
          </h1>
          <p className="text-muted-foreground mt-1">
            Calculez les aides et la marge selon le type de chantier
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Paramètres du chantier */}
        <div className="border border-border rounded-sm bg-card p-6 space-y-5">
          <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
            <Home className="w-5 h-5 text-primary" />
            Paramètres du chantier
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Zone géographique
              </Label>
              <Select value={zone} onValueChange={setZone}>
                <SelectTrigger className="rounded-sm mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="H1">H1 - Nord et Est</SelectItem>
                  <SelectItem value="H2">H2 - Ouest et Centre</SelectItem>
                  <SelectItem value="H3">H3 - Sud et Méditerranée</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="flex items-center gap-2">
                <Ruler className="w-4 h-4" />
                Surface chauffée
              </Label>
              <Select value={surface} onValueChange={setSurface}>
                <SelectTrigger className="rounded-sm mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="S < 70">Moins de 70 m²</SelectItem>
                  <SelectItem value="70 ≤ S < 90">Entre 70 et 90 m²</SelectItem>
                  <SelectItem value="90 ≤ S">90 m² et plus</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Type de chantier</Label>
              <Select value={typeChantier} onValueChange={setTypeChantier}>
                <SelectTrigger className="rounded-sm mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PAC">PAC seule</SelectItem>
                  <SelectItem value="PAC/SSC">PAC + SSC</SelectItem>
                  <SelectItem value="PAC + CESI">PAC + CESI</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Précarité énergétique</Label>
              <div className="grid grid-cols-2 gap-3 mt-1.5">
                <Button
                  type="button"
                  variant={precarite === "bleu" ? "default" : "outline"}
                  onClick={() => setPrecarite("bleu")}
                  className={cn(
                    "rounded-sm",
                    precarite === "bleu" && "bg-blue-500 hover:bg-blue-600"
                  )}
                >
                  Bleu (Très Modeste)
                </Button>
                <Button
                  type="button"
                  variant={precarite === "jaune" ? "default" : "outline"}
                  onClick={() => setPrecarite("jaune")}
                  className={cn(
                    "rounded-sm",
                    precarite === "jaune" && "bg-amber-500 hover:bg-amber-600"
                  )}
                >
                  Jaune (Modeste)
                </Button>
              </div>
            </div>

            {isAdmin && (
            <div>
              <Label>Période MPR</Label>
              <button
                type="button"
                onClick={() => setPeriodeMPRAvant(!periodeMPRAvant)}
                className={cn("flex items-center gap-3 w-full mt-1.5 p-3 rounded-lg border-2 transition-all", periodeMPRAvant ? "border-amber-400 bg-amber-500/10" : "border-border bg-card")}
                data-testid="switch-periode-mpr"
              >
                {periodeMPRAvant ? <ToggleRight className="w-6 h-6 text-amber-500" /> : <ToggleLeft className="w-6 h-6 text-muted-foreground" />}
                <div className="text-left">
                  <p className="text-sm font-semibold">{periodeMPRAvant ? "Avant 31/12/25" : "Après 31/12/25"}</p>
                </div>
              </button>
            </div>
            )}
          </div>
        </div>

        {/* Charges (B21-B25) */}
        <div className="border border-border rounded-sm bg-card p-6 space-y-5">
          <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
            <Euro className="w-5 h-5 text-primary" />
            Charges ({typeChantier})
          </h2>
          
          <div className="space-y-4">
            <div>
              <Label>Marchandise (€)</Label>
              <Input
                type="number"
                value={typeChantier === "PAC" ? charges.marchandise_pac : 
                       typeChantier === "PAC/SSC" ? charges.marchandise_pac_ssc : 
                       charges.marchandise_pac_cesi}
                onChange={(e) => handleChargeChange(
                  typeChantier === "PAC" ? "marchandise_pac" : 
                  typeChantier === "PAC/SSC" ? "marchandise_pac_ssc" : 
                  "marchandise_pac_cesi", 
                  e.target.value
                )}
                className="rounded-sm mt-1.5"
              />
            </div>

            <div>
              <Label>Sous-traitant (€)</Label>
              <Input
                type="number"
                value={typeChantier === "PAC" ? charges.sous_traitant_pac : 
                       typeChantier === "PAC/SSC" ? charges.sous_traitant_pac_ssc : 
                       charges.sous_traitant_pac_cesi}
                onChange={(e) => handleChargeChange(
                  typeChantier === "PAC" ? "sous_traitant_pac" : 
                  typeChantier === "PAC/SSC" ? "sous_traitant_pac_ssc" : 
                  "sous_traitant_pac_cesi", 
                  e.target.value
                )}
                className="rounded-sm mt-1.5"
              />
            </div>

            <div>
              <Label>Mode de coût</Label>
              <div className="flex gap-1 mt-1.5">
                <Button type="button" variant={regieMode === "regie" ? "default" : "outline"} onClick={() => setRegieMode("regie")} className="rounded-sm text-xs" size="sm" data-testid="mode-regie">Régie</Button>
                <Button type="button" variant={regieMode === "lead" ? "default" : "outline"} onClick={() => setRegieMode("lead")} className="rounded-sm text-xs" size="sm" data-testid="mode-lead">Lead</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Régie MPR : {regieMPR.toLocaleString('fr-FR')} €</p>
            </div>

            <div>
              <Label>Admin (€)</Label>
              <Input
                type="number"
                value={charges.admin}
                onChange={(e) => handleChargeChange("admin", e.target.value)}
                className="rounded-sm mt-1.5"
              />
            </div>

            <div className="pt-3 border-t border-border">
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total Charges</span>
                <span className="text-lg font-bold">{resultats?.totalCharges?.toLocaleString('fr-FR')} €</span>
              </div>
            </div>
          </div>
        </div>

        {/* Résultats */}
        <div className="border border-border rounded-sm bg-card p-6 space-y-5">
          <h2 className="font-heading font-semibold text-lg flex items-center gap-2 border-b border-border pb-3">
            <TrendingUp className="w-5 h-5 text-primary" />
            Résultats
          </h2>
          
          {resultats && (
            <div className="space-y-4">
              {/* MPR */}
              <div className={cn(
                "p-4 rounded-sm border",
                precarite === "bleu" ? "bg-blue-500/10 border-blue-500/30" : "bg-amber-500/10 border-amber-500/30"
              )}>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">MPR (Ma Prime Rénov')</span>
                  <span className={cn(
                    "text-xl font-bold",
                    precarite === "bleu" ? "text-blue-700" : "text-amber-700"
                  )}>
                    {resultats.mpr.toLocaleString('fr-FR')} €
                  </span>
                </div>
              </div>

              {/* CEE */}
              <div className={cn(
                "p-4 rounded-sm border",
                precarite === "bleu" ? "bg-blue-500/10 border-blue-500/30" : "bg-amber-500/10 border-amber-500/30"
              )}>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">CEE</span>
                  <span className={cn(
                    "text-xl font-bold",
                    precarite === "bleu" ? "text-blue-700" : "text-amber-700"
                  )}>
                    {resultats.cee.toLocaleString('fr-FR')} €
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {resultats.montantKwhc.toLocaleString('fr-FR')} kWhc × {precarite === "bleu" ? "0,0125" : "0,0074"} €
                </p>
              </div>

              {/* Total Aides */}
              <div className="p-4 rounded-sm border border-primary/30 bg-primary/5">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Total Aides</span>
                  <span className="text-xl font-bold text-primary">
                    {resultats.totalAides.toLocaleString('fr-FR')} €
                  </span>
                </div>
              </div>

              {/* Financement MPR */}
              <div className="p-3 rounded-sm bg-muted/50 border border-border">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-muted-foreground">Financement MPR (12%)</span>
                  <span className="font-medium">-{resultats.financementMPR.toLocaleString('fr-FR')} €</span>
                </div>
              </div>

              {/* MARGE */}
              <div className={cn(
                "p-5 rounded-sm border-2",
                resultats.marge >= 0 
                  ? "bg-emerald-500/10 border-emerald-500/40" 
                  : "bg-red-500/10 border-red-300"
              )}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    {resultats.marge >= 0 ? (
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    )}
                    <span className="font-semibold">MARGE à 1€</span>
                  </div>
                  <span className={cn(
                    "text-2xl font-bold",
                    resultats.marge >= 0 ? "text-emerald-700" : "text-red-700"
                  )}>
                    {resultats.marge.toLocaleString('fr-FR')} €
                  </span>
                </div>
              </div>

              {/* Info formule */}
              <div className="flex items-start gap-2 p-3 bg-muted/30 rounded-sm text-xs text-muted-foreground">
                <Info className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  Marge = (MPR + CEE) / 1,055 - Charges - Financement MPR
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tableau récapitulatif pour toutes les zones */}
      <div className="border border-border rounded-sm bg-card p-6">
        <h2 className="font-heading font-semibold text-lg mb-4">
          Récapitulatif {typeChantier} - Précarité {precarite === "bleu" ? "Bleu" : "Jaune"}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50">
                <th className="px-4 py-3 text-left font-semibold">Zone</th>
                <th className="px-4 py-3 text-left font-semibold">Surface</th>
                <th className={cn(
                  "px-4 py-3 text-right font-semibold",
                  precarite === "bleu" ? "bg-blue-100" : "bg-amber-100"
                )}>MPR</th>
                <th className={cn(
                  "px-4 py-3 text-right font-semibold",
                  precarite === "bleu" ? "bg-blue-100" : "bg-amber-100"
                )}>CEE</th>
                <th className="px-4 py-3 text-right font-semibold">Marge à 1€</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {["H1", "H2", "H3"].map(z => (
                ["S < 70", "70 ≤ S < 90", "90 ≤ S"].map((s, idx) => {
                  const montant = MONTANT_KWHC[z][s];
                  const prix = precarite === "bleu" ? PRIX_RACHAT_TRES_MODESTE : PRIX_RACHAT_CLASSIQUE;
                  const cee = montant * prix;
                  let mpr = 0;
                  let totalCharges = 0;
                  
                  if (typeChantier === "PAC") {
                    mpr = precarite === "bleu" ? MPR_PAC.bleu : MPR_PAC.jaune;
                    totalCharges = charges.marchandise_pac + charges.sous_traitant_pac + regieMPR + charges.admin;
                  } else if (typeChantier === "PAC/SSC") {
                    mpr = (precarite === "bleu" ? MPR_PAC.bleu : MPR_PAC.jaune) + 
                          (precarite === "bleu" ? MPR_SSC.bleu : MPR_SSC.jaune);
                    totalCharges = charges.marchandise_pac_ssc + charges.sous_traitant_pac_ssc + regieMPR + charges.admin;
                  } else {
                    mpr = (precarite === "bleu" ? MPR_PAC.bleu : MPR_PAC.jaune) + 
                          (precarite === "bleu" ? MPR_CESI.bleu : MPR_CESI.jaune);
                    totalCharges = charges.marchandise_pac_cesi + charges.sous_traitant_pac_cesi + regieMPR + charges.admin;
                  }
                  
                  const finMPR = mpr * TAUX_FINANCEMENT_MPR;
                  const marge = ((mpr + cee) / 1.055) - totalCharges - finMPR;
                  
                  return (
                    <tr key={`${z}-${s}`} className={cn(
                      "hover:bg-muted/30",
                      zone === z && surface === s && "bg-primary/10"
                    )}>
                      <td className="px-4 py-3 font-medium">{idx === 0 ? z : ""}</td>
                      <td className="px-4 py-3">{s}</td>
                      <td className="px-4 py-3 text-right tabular-nums">{mpr.toLocaleString('fr-FR')} €</td>
                      <td className="px-4 py-3 text-right tabular-nums">{Math.round(cee).toLocaleString('fr-FR')} €</td>
                      <td className={cn(
                        "px-4 py-3 text-right font-semibold tabular-nums",
                        marge >= 0 ? "text-emerald-600" : "text-red-600"
                      )}>
                        {Math.round(marge).toLocaleString('fr-FR')} €
                      </td>
                    </tr>
                  );
                })
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Frise chronologique MPR */}
      {resultats && (() => {
        const { timelines, totalDays } = buildTimelinesForProgram({ programType: "mpr" });
        return <TimelineDisplay timelines={timelines} totalDays={totalDays} />;
      })()}
    </div>
  );
}
